import React, { useState, useEffect, useLayoutEffect } from 'react';
import DatePicker from './DatePicker';
import { 
  getLocationContact, 
  formatBookingEmail, 
  formatConfirmationEmail, 
  validateBookingForm
} from '../utils/bookingHelpers';

const BookingModal = ({ isOpen, onClose, isFromRepair = false, repairDetails = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: 'Alpharetta',
    selectedDate: '',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: 'Alpharetta',
        selectedDate: '',
        notes: ''
      });
      setErrors({});
      setSubmitStatus('');
    } else {
      // Restore scroll position when modal closes
      const y = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(y || '0') * -1);
    }
  }, [isOpen, repairDetails]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      if (field === 'location') {
        updated.selectedDate = '';
      }

      return updated;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateBookingForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Prepare Formspree data
      const formspreeData = {
        name: formData.name,
        email: formData.email, // Required for auto-response to work
        _replyto: formData.email, // Sets the reply-to address
        phone: formData.phone,
        location: formData.location,
        appointment_date: formData.selectedDate,
        appointment_type: isFromRepair ? 'Repair Service' : 'General Consultation',
        notes: formData.notes,
        
        // Include a formatted subject for email notifications
        _subject: `New Appointment Request - ${formData.location} - ${formData.selectedDate}`,
        
        // Form type identifier to differentiate from contact form
        form_type: 'appointment_booking',
        
        // Full appointment details in message field
        message: formatBookingEmail(formData, repairDetails)
      };

      // Add repair details as separate fields if available
      if (repairDetails) {
        formspreeData.service_category = repairDetails.category;
        formspreeData.service_name = repairDetails.service;
        formspreeData.service_branch = repairDetails.branch || '';
        formspreeData.estimated_price = repairDetails.price;
      }

      const response = await fetch(process.env.REACT_APP_FORMSPREE_APPOINTMENT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formspreeData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
        console.error('Formspree error:', result);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const locationContact = formData.location ? getLocationContact(formData.location) : null;

  // Use useLayoutEffect to lock scroll before DOM updates
  useLayoutEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      setShouldRender(true);
    } else {
      setShouldRender(false);
    }
  }, [isOpen]);

  if (!isOpen || !shouldRender) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/50 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] bg-white rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-y-auto max-h-[95vh] sm:max-h-[90vh] overscroll-behavior-contain touch-pan-y">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gold-400/20">
          <div className="flex items-center justify-between">
            <h2 className="font-playfair text-xl sm:text-2xl text-charcoal pr-4">
              Book Your {isFromRepair ? 'Repair' : ''} Appointment
            </h2>
            <button
              onClick={onClose}
              className="text-charcoal/60 hover:text-charcoal text-2xl flex-shrink-0 w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>
          </div>
          {isFromRepair && repairDetails && (
            <p className="font-montserrat text-sm text-charcoal/70 mt-2">
              Service: {repairDetails.category} → {repairDetails.service} → Estimated: {repairDetails.price}
            </p>
          )}
        </div>

        {submitStatus === 'success' ? (
          /* Success Message */
          <div className="p-8 text-center">
            <div className="text-6xl text-green-500 mb-4">✓</div>
            <h3 className="font-playfair text-2xl text-charcoal mb-4">Appointment Confirmed!</h3>
            <p className="font-montserrat text-charcoal/70 mb-6">
              You'll receive a confirmation email shortly.
            </p>
            {locationContact && (
              <div className="bg-gold-50 p-4 rounded-lg mb-6">
                <p className="font-montserrat text-sm text-charcoal">
                  <strong>Need to modify your appointment?</strong><br />
                  Call {formData.location}: <span className="font-semibold">{locationContact.phone}</span>
                </p>
              </div>
            )}
            <button
              onClick={onClose}
              className="px-8 py-3 bg-gradient-gold text-white font-montserrat text-sm tracking-wider"
            >
              CLOSE
            </button>
          </div>
        ) : (
          /* Booking Form */
          <form onSubmit={handleSubmit} className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
              {/* Left Column - Form */}
              <div className="space-y-4 sm:space-y-6">
                <h3 className="font-playfair text-lg sm:text-xl text-charcoal">Contact Information</h3>
                
                {/* Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full p-3 sm:p-4 border rounded-lg ${
                      errors.name ? 'border-red-400 focus:border-red-400' : 'border-gold-400/30 focus:border-gold-400'
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full p-3 sm:p-4 border rounded-lg ${
                      errors.email ? 'border-red-400 focus:border-red-400' : 'border-gold-400/30 focus:border-gold-400'
                    }`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number *"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full p-3 sm:p-4 border rounded-lg ${
                      errors.phone ? 'border-red-400 focus:border-red-400' : 'border-gold-400/30 focus:border-gold-400'
                    }`}
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                {/* Location */}
                <div>
                  <select
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className={`w-full p-3 sm:p-4 border rounded-lg ${
                      errors.location ? 'border-red-400 focus:border-red-400' : 'border-gold-400/30 focus:border-gold-400'
                    }`}
                  >
                    <option value="">Select Location *</option>
                    <option value="Alpharetta">Alpharetta - North Point</option>
                    <option value="Cumming">Cumming - The Collection</option>
                  </select>
                  {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
                </div>

                {/* Notes */}
                <div>
                  <textarea
                    placeholder="Additional notes (optional)"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows="4"
                    className="w-full p-3 sm:p-4 border border-gold-400/30 focus:border-gold-400 rounded-lg"
                  />
                </div>
              </div>

              {/* Right Column - Calendar & Location Info */}
              <div className="space-y-4 sm:space-y-6">
                <h3 className="font-playfair text-lg sm:text-xl text-charcoal">Select Date</h3>
                
                <DatePicker
                  selectedDate={formData.selectedDate}
                  onDateSelect={(date) => handleInputChange('selectedDate', date)}
                  location={formData.location}
                  isFromRepair={isFromRepair}
                />
                
                {errors.selectedDate && (
                  <p className="text-red-500 text-sm text-center">{errors.selectedDate}</p>
                )}

                {/* Location Info */}
                {locationContact && (
                  <div className="bg-cream p-4 rounded-lg">
                    <h4 className="font-playfair text-lg text-charcoal mb-2">Location Details</h4>
                    <div className="space-y-1 text-sm font-montserrat text-charcoal/70">
                      <p>{locationContact.address}</p>
                      <p>Phone: {locationContact.phone}</p>
                      <p>Hours: {locationContact.hours}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gold-400/20">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:flex-1 py-3 sm:py-4 bg-gradient-gold text-white font-montserrat text-sm tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'SENDING...' : 'BOOK APPOINTMENT'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 border border-gold-400 text-gold-600 font-montserrat text-sm tracking-wider hover:bg-gold-50"
              >
                CANCEL
              </button>
            </div>

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm text-center">
                  There was an error sending your appointment request. Please try again or call us directly.
                </p>
              </div>
            )}
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
